import type { NodeEntry } from 'slate';
import { Editor, Node, Path, Point } from 'slate';

import { breakLine } from './break-line';
import { buildRenderModelLine } from './render-model-line';
import type { ParagraphElement, RenderModelLine, RenderModelParagraph } from '../internal';
import {
  adjustActiveTabsRelativeToLine,
  buildActiveTabs,
  getParagraphHeightPx,
  isParagraphElement,
  Queries,
} from '../internal';

export function buildRenderModelParagraph(
  editor: Editor,
  paragraphEntry: NodeEntry<ParagraphElement>,
  options: { containerWidthPx: number }
): RenderModelParagraph {
  const { containerWidthPx } = options;
  const [paragraph, paragraphPath] = paragraphEntry;
  const paragraphRange = Editor.range(editor, paragraphPath);
  const relativeParagraphRange = Queries.relativeRange(paragraphRange, paragraphPath);
  const listMarker = Queries.listMarker(editor, paragraphEntry);
  const isJustified = Queries.renderedParagraphAlignment(paragraph) === 'justify';
  const paragraphWidthPx = Queries.renderedParagraphWidth(
    { containerWidthPx, paragraphProps: paragraph },
    'px'
  );

  // If the paragraph is a list item, the first line has extra padding for the marker.
  const { firstLineIndentAdjustedValPx } = Queries.adjustedFirstLineIndentPx(editor, {
    listMarker,
    paragraph,
  });

  // Determine tab stops for first and non-first lines in the paragraph
  const [document] = Queries.getDocument(editor);
  const activeDefaultTabStopPx = Queries.activeDefaultTabStopPx(document);
  const activeIndentationLeftPx = Queries.renderedParagraphLeftIndent(paragraph, 'px');

  const activeParagraphTabs = buildActiveTabs({
    defaultTabWidth: activeDefaultTabStopPx,
    indentationFirstLine: firstLineIndentAdjustedValPx,
    indentationLeft: activeIndentationLeftPx,
    innerWidth: paragraphWidthPx,
    tabStops: [],
  });

  const { first: adjustedFirstLineTabs, rest: adjustedRestLineTabs } =
    adjustActiveTabsRelativeToLine(activeParagraphTabs, {
      activeIndentationFirstLine: firstLineIndentAdjustedValPx,
      activeIndentationLeft: activeIndentationLeftPx,
    });

  /**
   * Iteratively break the line where necessary and construct one or more render model line
   * elements.
   */
  const rMLines: RenderModelLine[] = [];
  let nextLineIndex = 0;
  let nextLineRelativeStart: Point | null = relativeParagraphRange.anchor;

  while (nextLineRelativeStart) {
    const lineRelativeStart = nextLineRelativeStart;
    const lineIndex = nextLineIndex++;
    const isFirstLine = lineIndex === 0;
    const maxLineWidthPx = paragraphWidthPx - (isFirstLine ? firstLineIndentAdjustedValPx : 0);

    // Determine where the line should break and the widths of all tabs on the line
    const { endPoint: lineRelativeEnd, tabWidthsPx } = breakLine(editor, {
      maxLineWidthPx,
      paragraph,
      startPoint: lineRelativeStart,
      tabStopsPx: isFirstLine ? adjustedFirstLineTabs : adjustedRestLineTabs,
    });

    nextLineRelativeStart = lineRelativeEnd;
    const isLastLine = !lineRelativeEnd;

    // Make sure we don't get into an infinite loop
    if (!isLastLine && !Point.isAfter(lineRelativeEnd, lineRelativeStart)) {
      throw new Error(
        `breakLine returned a break at the start of the line: ${JSON.stringify(lineRelativeStart)}`
      );
    }

    rMLines.push(
      buildRenderModelLine(editor, {
        isFirstLine,
        isJustified: isJustified && !isLastLine,
        isLastLine,
        listMarker: isFirstLine ? listMarker : null,
        maxLineWidthPx,
        paragraph,
        range: {
          anchor: lineRelativeStart,
          focus: lineRelativeEnd ?? relativeParagraphRange.focus,
        },
        tabWidthsPx,
      })
    );
  }

  const previousSibling = Path.hasPrevious(paragraphPath)
    ? (Node.getIf(editor, Path.previous(paragraphPath)) ?? null)
    : null;
  const nextSibling = Node.getIf(editor, Path.next(paragraphPath)) ?? null;

  const prevSharesStyle = sharesParagraphStyle(paragraph, previousSibling);
  const prevParaSpacingAfterPx = getPrevParaSpacingAfterPx(previousSibling, prevSharesStyle);

  function borderSibling(node: Node | null) {
    if (!node || !isParagraphElement(node)) return null;
    return {
      borders: Queries.renderedParagraphBorders(node),
      firstLineIndentPx: Queries.renderedParagraphFirstLineIndent(node, 'px'),
      /**
       * TODO: This assumes that the given paragraph has the same container width as that which
       * we're building.
       */
      widthPx: Queries.renderedParagraphWidth({ containerWidthPx, paragraphProps: node }, 'px'),
    };
  }

  const paragraphBordersToDraw = Queries.getParagraphBordersToDraw({
    borders: Queries.renderedParagraphBorders(paragraph),
    firstLineIndentPx: Queries.renderedParagraphFirstLineIndent(paragraph, 'px'),
    next: borderSibling(nextSibling),
    prev: borderSibling(previousSibling),
    widthPx: paragraphWidthPx,
  });

  const borderInsets = Queries.renderedParagraphBorderLayoutInsets({
    bordersToDraw: paragraphBordersToDraw.bordersToDraw,
    isLastFragment: true,
    splitIndex: 0,
  });

  const rMParagraph: RenderModelParagraph = {
    ...paragraph,
    borderLayoutBottomPx: borderInsets.bottom,
    borderLayoutTopPx: borderInsets.top,
    borderMergesWithNext: paragraphBordersToDraw.mergesWithNext,
    borderMergesWithPrev: paragraphBordersToDraw.mergesWithPrev,
    bordersToDraw: paragraphBordersToDraw.bordersToDraw,
    children: rMLines,
    /** Set below. */
    heightPx: 0,
    key: Queries.findKey(paragraph).id,
    listMarker,
    paragraphHasBreaks: rMLines.some(line => !!line.contentBreak) || undefined,
    prevParaSpacingAfterPx,
    relativePath: Queries.getPathRelativeToParent(paragraphPath),
    spacingAfterSuppressed:
      Boolean(paragraph.contextualSpacing) && sharesParagraphStyle(paragraph, nextSibling),
    spacingBeforeSuppressed: Boolean(paragraph.contextualSpacing) && prevSharesStyle,
    splitIndex: 0,
    widthPx: paragraphWidthPx,
  };

  rMParagraph.heightPx = getParagraphHeightPx(rMParagraph, {
    bottomPx: borderInsets.bottom,
    topPx: borderInsets.top,
  });

  return rMParagraph;
}

function sharesParagraphStyle(paragraph: ParagraphElement, neighbour: Node | null): boolean {
  if (!neighbour || !isParagraphElement(neighbour)) return false;

  return (
    Queries.renderedParagraphStyleVariant(paragraph) ===
    Queries.renderedParagraphStyleVariant(neighbour)
  );
}

function getPrevParaSpacingAfterPx(
  prevParagraph: Node | null,
  prevSharesStyle: boolean
): number | null {
  if (!prevParagraph || !isParagraphElement(prevParagraph)) return null;
  if (prevSharesStyle && prevParagraph.contextualSpacing) return 0;

  return Queries.renderedParagraphSpacingAfter(prevParagraph, 'px');
}
